import React, { useEffect, useMemo, useState } from 'react';
import './ProductPriceGroupForm.css';
import '../../../shared/StyledInput.css';
import { suggestShortCode } from '../shared/priceGroupShortCode';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';

// Groups at/under `initial` can't be picked as its own parent (would create a cycle)
function getSelfAndDescendantIds(groups, id) {
  const ids = new Set([id]);
  const idx = groups.findIndex((g) => g.id === id);
  if (idx === -1) return ids;
  const nodeDepth = groups[idx].depth;
  for (let i = idx + 1; i < groups.length; i++) {
    if (groups[i].depth <= nodeDepth) break;
    ids.add(groups[i].id);
  }
  return ids;
}

const INDENT = '   ';

function ProductPriceGroupForm({ initial, groups, onSave, onCancel, loading }) {
  const [name, setName] = useState(initial?.name || '');
  const [parentId, setParentId] = useState(initial?.parent_id ? String(initial.parent_id) : '');
  const [shortCode, setShortCode] = useState(initial?.short_code || '');
  const [shortCodeEdited, setShortCodeEdited] = useState(false);
  const [error, setError] = useState('');

  const excludedIds = useMemo(
    () => (initial ? getSelfAndDescendantIds(groups, initial.id) : new Set()),
    [initial, groups]
  );

  const parentOptions = groups.filter((g) => g.is_active && !excludedIds.has(g.id));

  const existingCodes = useMemo(
    () => groups.filter((g) => !initial || g.id !== initial.id).map((g) => g.short_code),
    [groups, initial]
  );

  // Only auto-suggest for a brand-new group, and only until the user types
  // into the field themselves — after that it's theirs to control.
  useEffect(() => {
    if (initial || shortCodeEdited) return;
    setShortCode(suggestShortCode(name, existingCodes));
  }, [name, initial, shortCodeEdited, existingCodes]);

  const validate = () => {
    if (!name.trim()) return 'Group name is required.';
    if (!/^[A-Z0-9]{1,5}$/.test(shortCode.trim().toUpperCase())) return 'Short code is required (1-5 letters/numbers).';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      name: name.trim(),
      parent_id: parentId ? Number(parentId) : null,
      short_code: shortCode.trim().toUpperCase(),
    });
  };

  return (
    <form className="product-price-group-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label>Group Name<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g., Connectors"
          />
        </div>
        <div>
          <label>Parent Group</label><br />
          <select className="styled-input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">(none — top level)</option>
            {parentOptions.map((g) => (
              <option key={g.id} value={g.id}>{INDENT.repeat(g.depth)}{g.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Short Code<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={shortCode}
            onChange={(e) => { setShortCode(e.target.value.toUpperCase()); setShortCodeEdited(true); }}
            maxLength={5}
            placeholder="e.g., CON"
            style={{ width: 70, textTransform: 'uppercase' }}
          />
        </div>
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default ProductPriceGroupForm;
